import React, { useEffect, useLayoutEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { SITE } from "@/data/content";
import { lockPageScroll, unlockPageScroll } from "@/lib/scrollLock";
import { LanguageToggle, ThemeToggle } from "./LangThemeSwitcher";
import Logo from "./Logo";

const NAV_MENU_LOCK_ID = "mobile-menu";

const Nav = ({ onOpenOrder }) => {
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useLayoutEffect(() => {
    if (open) lockPageScroll(NAV_MENU_LOCK_ID);
    else unlockPageScroll(NAV_MENU_LOCK_ID);
    return () => unlockPageScroll(NAV_MENU_LOCK_ID);
  }, [open]);

  const closeMenu = () => setOpen(false);
  const smoothScrollToSection = (sectionId) => {
    const target = document.getElementById(sectionId);
    if (!target) return;
    const nav = document.querySelector("[data-testid='site-nav']");
    const navHeight = nav?.getBoundingClientRect().height || 0;
    const sectionTop = target.getBoundingClientRect().top + window.scrollY;
    const targetTop = Math.max(0, sectionTop - navHeight - 16);
    window.scrollTo({ top: targetTop, behavior: "smooth" });
  };
  const goToSection = (sectionId, closeAfter = false) => {
    const targetHash = `#${sectionId}`;
    const onHome = location.pathname === "/";
    if (closeAfter) closeMenu();
    if (onHome) {
      navigate(`/${targetHash}`);
      const delay = closeAfter ? 220 : 0;
      window.setTimeout(() => smoothScrollToSection(sectionId), delay);
      return;
    }
    navigate(`/${targetHash}`);
  };
  const handleMobileBuy = () => {
    closeMenu();
    window.setTimeout(() => {
      onOpenOrder?.();
    }, 220);
  };

  const items = [
    { key: "home", href: "#home" },
    { key: "about", href: "#about" },
    { key: "gallery", href: "#gallery" },
    { key: "pricing", href: "#pricing" },
    { key: "faq", href: "#faq" },
    { key: "contact", href: "#contact" },
  ];

  return (
    <motion.header
      initial={{ y: -32, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "py-2.5" : "py-4"}`}
      data-testid="site-nav"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className={`glass rounded-2xl flex items-center justify-between px-3 sm:px-5 py-2.5 sm:py-3 gap-2`}>
          <button
            type="button"
            onClick={() => goToSection("home")}
            className="flex items-center group flex-shrink-0"
            data-testid="nav-logo"
            aria-label="MAX Fitness"
          >
            <Logo className="h-7 sm:h-9 w-auto" />
          </button>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-7" data-testid="nav-desktop">
            {items.map((it) => (
              <button
                key={it.key}
                type="button"
                onClick={() => goToSection(it.href.replace("#", ""))}
                data-testid={`nav-link-${it.key}`}
                className="text-sm text-[var(--text-dim)] hover:text-[var(--text)] transition-colors duration-200 relative group"
              >
                {t(`nav.${it.key}`)}
                <span className="absolute -bottom-1.5 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[var(--accent)] to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <a
              href={`tel:${SITE.phone.replace(/\s/g, "")}`}
              data-testid="nav-phone"
              className="hidden xl:inline-flex items-center gap-2 text-sm font-medium text-[var(--text)] hover:text-[var(--text)] px-3 py-2 rounded-full hover:bg-[var(--surface-2)] transition-colors"
            >
              <Phone size={14} className="text-[var(--accent)]" />
              <span className="tabular-nums">{SITE.phone}</span>
            </a>
            <LanguageToggle />
            <ThemeToggle />
            <button
              type="button"
              onClick={onOpenOrder}
              className="btn-primary text-sm hidden sm:inline-flex !py-2.5 !px-4"
              data-testid="nav-cta"
            >
              {t("nav.buy")}
            </button>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="lg:hidden w-9 h-9 rounded-full glass flex items-center justify-center"
              aria-label={t("nav.openMenu")}
              data-testid="nav-menu-button"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[60] lg:hidden"
            data-testid="nav-mobile-overlay"
            data-overlay-root="true"
            data-overlay-name={NAV_MENU_LOCK_ID}
          >
            <div className="absolute inset-0 bg-black/70 sm:backdrop-blur-md" onClick={closeMenu} />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="mobile-performance-surface mobile-performance-surface-soft absolute right-0 top-0 bottom-0 w-[85%] max-w-sm glass-strong p-5 flex flex-col gap-1.5"
              data-scroll-lock-allow="true"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <button
                  type="button"
                  onClick={() => goToSection("home", true)}
                  aria-label="MAX Fitness"
                  data-testid="nav-mobile-logo"
                >
                  <Logo className="h-7 w-auto" />
                </button>
                <button
                  type="button"
                  onClick={closeMenu}
                  className="w-9 h-9 rounded-full glass flex items-center justify-center"
                  aria-label={t("nav.closeMenu")}
                  data-testid="nav-mobile-close"
                >
                  <X size={18} />
                </button>
              </div>
              {items.map((it) => (
                <button
                  key={it.key}
                  type="button"
                  onClick={() => goToSection(it.href.replace("#", ""), true)}
                  className="py-3 px-4 rounded-xl hover:bg-[var(--surface-2)] text-base"
                  data-testid={`nav-mobile-${it.key}`}
                >
                  {t(`nav.${it.key}`)}
                </button>
              ))}
              <button
                type="button"
                onClick={handleMobileBuy}
                className="btn-primary mt-3 inline-flex justify-center"
                data-testid="nav-mobile-cta"
              >
                {t("nav.buy")}
              </button>
              <div className="mt-auto pt-5 border-t border-[var(--border)]">
                <a href={`tel:${SITE.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 px-4 py-3 rounded-xl hover:bg-[var(--surface-2)]">
                  <Phone size={16} className="text-[var(--accent)]" />
                  <span className="tabular-nums">{SITE.phone}</span>
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Nav;
